import { z } from 'zod';
import type { LocalizedText } from './permissions.ts';

// ---------------------------------------------------------------------------
// Biblioteca Jurídica: Minutas Padrão (ADR 0038)
// ---------------------------------------------------------------------------

export const TEMPLATE_LANGUAGES = ['pt-BR', 'en', 'es', 'bilingual'] as const;
export const TEMPLATE_JURISDICTIONS = ['domestic', 'international'] as const;
export const TEMPLATE_STRUCTURES = ['bilateral', 'unilateral'] as const;
/** Situação do modelo: em uso ou retirado (o histórico continua). */
export const TEMPLATE_STATUSES = ['active', 'retired'] as const;
/**
 * Situação de cada versão: rascunho → enviada para aprovação → aprovada (vigente) ou devolvida;
 * a aprovada anterior vira substituída. Versão aprovada não muda.
 */
export const TEMPLATE_VERSION_STATUSES = [
  'draft',
  'submitted',
  'approved',
  'returned',
  'superseded',
] as const;
export type TemplateVersionStatus = (typeof TEMPLATE_VERSION_STATUSES)[number];

export interface TemplateField {
  key: string;
  group:
    'contract' | 'loot' | 'counterparty' | 'request' | 'signature' | 'payment' | 'bank' | 'service';
  label: LocalizedText;
  /** Dado pessoal: o preenchimento futuro segue as regras de proteção (CPF cifrado). */
  personal?: boolean;
}

const f = (
  key: string,
  group: TemplateField['group'],
  pt: string,
  en: string,
  personal = false,
): TemplateField => ({ key, group, label: { 'pt-BR': pt, en }, ...(personal ? { personal } : {}) });

/**
 * Dicionário oficial dos campos preenchíveis das minutas (`{{chave}}`). É a lista do que o
 * "Gerar Contrato" poderá preencher a partir da solicitação, do contrato e dos cadastros; a
 * minuta não muda nenhuma cláusula, só recebe esses valores.
 */
export const TEMPLATE_FIELDS: readonly TemplateField[] = [
  f('contrato.codigo', 'contract', 'Código do contrato (CTR-AAAA-NNNNNN)', 'Contract code'),
  f(
    'contrato.original.codigo',
    'contract',
    'Código do contrato original (aditivo, distrato)',
    'Original contract code',
  ),
  f('contrato.titulo', 'contract', 'Título do contrato', 'Contract title'),
  f('contrato.objeto', 'contract', 'Objeto (descrição da solicitação)', 'Subject matter'),
  f('contrato.valor', 'contract', 'Valor com moeda', 'Amount with currency'),
  f('contrato.valor_extenso', 'contract', 'Valor por extenso', 'Amount in words'),
  f('contrato.moeda', 'contract', 'Moeda', 'Currency'),
  f('contrato.periodicidade', 'contract', 'Periodicidade do pagamento', 'Payment frequency'),
  f('contrato.parcelas', 'contract', 'Número de parcelas', 'Number of installments'),
  f('contrato.prazo_tipo', 'contract', 'Tipo de prazo', 'Term type'),
  f('contrato.inicio', 'contract', 'Início da vigência', 'Start date'),
  f('contrato.termino', 'contract', 'Término da vigência', 'End date'),
  f('contrato.renovacao_automatica', 'contract', 'Renovação automática', 'Automatic renewal'),
  f(
    'contrato.aviso_nao_renovacao_dias',
    'contract',
    'Aviso de não renovação (dias)',
    'Non-renewal notice (days)',
  ),
  f('contrato.foro', 'contract', 'Foro ou solução de conflitos', 'Forum or dispute resolution'),
  f(
    'loot.empresa.razao_social',
    'loot',
    'Razão social da empresa da LOOT',
    'LOOT company legal name',
  ),
  f('loot.empresa.cnpj', 'loot', 'CNPJ da empresa da LOOT', 'LOOT company tax ID'),
  f('loot.empresa.endereco', 'loot', 'Endereço da empresa da LOOT', 'LOOT company address'),
  f('loot.signatario', 'loot', 'Quem assina pela LOOT', 'LOOT signatory'),
  f(
    'loot.signatario.qualificacao',
    'loot',
    'Condição de representação de quem assina pela LOOT',
    'LOOT signatory capacity',
  ),
  f('loot.testemunha.nome', 'signature', 'Testemunha indicada pela LOOT', 'Witness named by LOOT'),
  f('loot.testemunha.cpf', 'signature', 'CPF da testemunha da LOOT', 'LOOT witness CPF', true),
  f('contraparte.nome', 'counterparty', 'Nome ou razão social da contraparte', 'Counterparty name'),
  f(
    'contraparte.documento',
    'counterparty',
    'CNPJ, EIN ou registro da contraparte',
    'Counterparty tax ID',
  ),
  f('contraparte.endereco', 'counterparty', 'Endereço da contraparte', 'Counterparty address'),
  f('contraparte.email', 'counterparty', 'E-mail da contraparte', 'Counterparty e-mail'),
  f(
    'contraparte.representante',
    'counterparty',
    'Representante da contraparte',
    'Counterparty representative',
  ),
  f(
    'contraparte.representante.qualificacao',
    'counterparty',
    'Qualificação do representante da contraparte',
    'Counterparty representative capacity',
  ),
  f(
    'contraparte.testemunha.nome',
    'signature',
    'Testemunha indicada pela contraparte',
    'Witness named by the counterparty',
  ),
  f(
    'contraparte.testemunha.cpf',
    'signature',
    'CPF da testemunha da contraparte',
    'Counterparty witness CPF',
    true,
  ),
  f(
    'solicitacao.protocolo',
    'request',
    'Protocolo da solicitação jurídica',
    'Legal request protocol',
  ),
  // Campos dos modelos convertidos em 28/10/2026 (pasta "Convertidos para o sistema"), consolidados.
  f(
    'contrato.aviso_previo_dias',
    'contract',
    'Aviso prévio de rescisão (dias)',
    'Termination notice (days)',
  ),
  f('loot.signatario.cpf', 'loot', 'CPF de quem assina pela LOOT', 'LOOT signatory CPF', true),
  f('loot.signatario.email', 'loot', 'E-mail de quem assina pela LOOT', 'LOOT signatory e-mail'),
  f('loot.gestor.nome', 'loot', 'Gestor do contrato na LOOT', 'LOOT contract manager'),
  f('loot.gestor.email', 'loot', 'E-mail do gestor do contrato', 'Contract manager e-mail'),
  f('loot.testemunha.email', 'signature', 'E-mail da testemunha da LOOT', 'LOOT witness e-mail'),
  f(
    'contraparte.representante.cpf',
    'counterparty',
    'CPF do representante da contraparte',
    'Counterparty representative CPF',
    true,
  ),
  f(
    'contraparte.representante.email',
    'counterparty',
    'E-mail do representante da contraparte',
    'Counterparty representative e-mail',
  ),
  f(
    'contraparte.profissional.nome',
    'counterparty',
    'Profissional indicado que executa',
    'Named professional',
  ),
  f(
    'contraparte.documento_pessoal',
    'counterparty',
    'Documento pessoal (RG, passaporte)',
    'Personal ID document',
    true,
  ),
  f('contraparte.data_nascimento', 'counterparty', 'Data de nascimento', 'Date of birth', true),
  f('contraparte.nacionalidade', 'counterparty', 'Nacionalidade', 'Nationality'),
  f(
    'contraparte.inscricao_fiscal',
    'counterparty',
    'Inscrição fiscal (municipal, VAT, tax ID)',
    'Tax registration',
  ),
  f(
    'contraparte.testemunha.email',
    'signature',
    'E-mail da testemunha da contraparte',
    'Counterparty witness e-mail',
  ),
  f('contraparte.banco.codigo', 'bank', 'Código do banco', 'Bank code'),
  f('contraparte.banco.nome', 'bank', 'Banco', 'Bank'),
  f('contraparte.banco.agencia', 'bank', 'Agência', 'Branch'),
  f('contraparte.banco.conta', 'bank', 'Conta', 'Account'),
  f('contraparte.banco.tipo_conta', 'bank', 'Tipo de conta', 'Account type'),
  f('contraparte.banco.titular', 'bank', 'Titular da conta', 'Account holder'),
  f('contraparte.banco.pix', 'bank', 'Chave PIX', 'PIX key'),
  f('contraparte.banco.iban', 'bank', 'IBAN', 'IBAN'),
  f('contraparte.banco.swift', 'bank', 'SWIFT/BIC', 'SWIFT/BIC'),
  f(
    'contraparte.banco.paypal',
    'bank',
    'PayPal ou plataforma de pagamento',
    'PayPal or payment platform',
  ),
  f(
    'pagamento.prazo_dias',
    'payment',
    'Prazo de pagamento depois da nota (dias)',
    'Payment term after invoice (days)',
  ),
  f('pagamento.dia_limite_nf', 'payment', 'Dia-limite para emitir a nota', 'Invoice cut-off day'),
  f(
    'pagamento.condicoes',
    'payment',
    'Condições de pagamento negociadas',
    'Negotiated payment terms',
  ),
  f('compra.numero', 'service', 'Número da compra (OCS)', 'Purchase number'),
  f(
    'servico.funcao',
    'service',
    'Função do profissional (2D, 3D, pintor…)',
    'Role (2D, 3D, painter…)',
  ),
  f('servico.escopo', 'service', 'Escopo do serviço', 'Scope of services'),
  f('servico.entregaveis', 'service', 'Entregáveis', 'Deliverables'),
  f('servico.especificacoes', 'service', 'Especificações técnicas', 'Technical specifications'),
  f('servico.local', 'service', 'Local do serviço', 'Place of service'),
  f('servico.data_inicio', 'service', 'Início do serviço', 'Service start'),
  f('servico.data_entrega', 'service', 'Entrega do serviço', 'Service delivery'),
  f('servico.criterio_aceite', 'service', 'Critério de aceite', 'Acceptance criteria'),
  f('servico.garantia_dias', 'service', 'Garantia (dias)', 'Warranty (days)'),
  f('servico.revisoes', 'service', 'Rodadas de revisão', 'Revision rounds'),
  f('assinatura.local', 'signature', 'Local da assinatura', 'Place of signature'),
  f('assinatura.data', 'signature', 'Data da assinatura', 'Date of signature'),
];
const FIELD_KEYS = new Set(TEMPLATE_FIELDS.map((x) => x.key));
const KEY_RE = /^[a-z][a-z0-9_]*(\.[a-z0-9_]+)*$/;

/** Texto corrido de um XML do Word (document, cabeçalhos e rodapés), sem as marcações. */
export function docxXmlText(xml: string): string {
  return xml
    .replace(/<w:tab\/>/g, ' ')
    .replace(/<\/w:p>/g, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&');
}

/**
 * Campos preenchíveis de uma minuta: `{{chave}}` (espaços ao redor da chave são aceitos).
 * - `fields`: chaves do dicionário oficial, sem repetição;
 * - `unknown`: chaves bem escritas que não estão no dicionário;
 * - `malformed`: marcações incompatíveis (chave com espaço, acento ou maiúscula, chaves soltas).
 */
export function templatePlaceholders(text: string): {
  fields: string[];
  unknown: string[];
  malformed: string[];
} {
  const fields = new Set<string>();
  const unknown = new Set<string>();
  const malformed = new Set<string>();
  const rest = text.replace(/\{\{([^{}\n]*)\}\}/g, (_m, inner: string) => {
    const key = inner.trim();
    if (!KEY_RE.test(key)) malformed.add(`{{${inner}}}`.slice(0, 120));
    else if (FIELD_KEYS.has(key) || clauseSlotPurpose(key)) fields.add(key);
    else unknown.add(key);
    return ' ';
  });
  for (const m of rest.matchAll(/\{\{[^\n]{0,40}|[^\n]{0,40}\}\}/g))
    malformed.add(m[0].trim().slice(0, 120));
  return { fields: [...fields].sort(), unknown: [...unknown].sort(), malformed: [...malformed] };
}

const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

/**
 * Tipos de item da Biblioteca Jurídica (ADR 0060): minuta padrão, cláusula padrão (texto) e os
 * modelos de NDA e de procuração (minutas das suas modalidades).
 */
export const LEGAL_LIBRARY_KINDS = ['template', 'clause', 'nda', 'power_of_attorney'] as const;
export type LegalLibraryKind = (typeof LEGAL_LIBRARY_KINDS)[number];

/** Modalidades permitidas em cada tipo de modelo (os demais tipos aceitam qualquer uma). */
export const LEGAL_LIBRARY_SERVICES: Partial<Record<LegalLibraryKind, readonly string[]>> = {
  nda: ['F11', 'F12', 'F13'],
  power_of_attorney: ['F17', 'F18'],
};

/** Finalidade das cláusulas padrão. */
export const LEGAL_CLAUSE_PURPOSES = [
  'confidentiality',
  'data_protection',
  'intellectual_property',
  'payment',
  'price_adjustment',
  'term_termination',
  'liability',
  'penalties',
  'warranties',
  'anticorruption',
  'exclusivity',
  'image_rights',
  'dispute_resolution',
  'general',
  'other',
] as const;

export const legalTemplateInput = z.object({
  kind: z.enum(LEGAL_LIBRARY_KINDS).default('template'),
  purpose: z.enum(LEGAL_CLAUSE_PURPOSES).nullable().optional(),
  name: z.string().trim().min(3).max(200),
  description: text(4000),
  serviceCodes: z
    .array(z.string().regex(/^F\d{2}$/))
    .min(1)
    .max(40)
    .transform((v) => [...new Set(v)].sort()),
  language: z.enum(TEMPLATE_LANGUAGES),
  jurisdiction: z.enum(TEMPLATE_JURISDICTIONS),
  structure: z.enum(TEMPLATE_STRUCTURES),
  legalEntityIds: z
    .array(z.uuid())
    .min(1)
    .max(50)
    .transform((v) => [...new Set(v)]),
  ownerUserId: z.uuid(),
  /** Minuta principal de que esta é anexo e como o anexo aparece (decisão de 28/10/2026). */
  annexOf: z.uuid().nullable().optional(),
  annexLabel: z.string().trim().min(1).max(80).nullable().optional(),
});
/** Confere o tipo: finalidade só (e sempre) na cláusula; modelos só nas suas modalidades. */
export function legalTemplateIssues(input: {
  kind: LegalLibraryKind;
  purpose?: string | null;
  serviceCodes: string[];
  annexOf?: string | null;
  annexLabel?: string | null;
}) {
  const issues: { path: string; message: string }[] = [];
  if (!!input.annexOf !== !!input.annexLabel) {
    issues.push({
      path: 'annexLabel',
      message: 'Informe a minuta principal e o nome do anexo juntos',
    });
  }
  if (input.annexOf && input.kind === 'clause') {
    issues.push({ path: 'annexOf', message: 'Cláusula não é anexo de minuta' });
  }
  if (input.kind === 'clause' && !input.purpose) {
    issues.push({ path: 'purpose', message: 'Informe a finalidade da cláusula' });
  }
  if (input.kind !== 'clause' && input.purpose) {
    issues.push({ path: 'purpose', message: 'Finalidade só nas cláusulas' });
  }
  const allowed = LEGAL_LIBRARY_SERVICES[input.kind];
  if (allowed && input.serviceCodes.some((c) => !allowed.includes(c))) {
    issues.push({ path: 'serviceCodes', message: `Use só as modalidades ${allowed.join(', ')}` });
  }
  return issues;
}

export type LegalTemplateInput = z.infer<typeof legalTemplateInput>;

/** Nova versão da cláusula padrão: o texto (nunca muda depois de gravado) e as observações. */
export const legalClauseVersionInput = z.object({
  body: z.string().trim().min(10).max(20000),
  changeNotes: text(4000),
});

export const legalTemplateVersionDecisionInput = z
  .object({
    action: z.enum(['approve', 'return']),
    note: text(2000),
  })
  .refine((v) => v.action !== 'return' || (v.note?.length ?? 0) >= 5, {
    path: ['note'],
    message: 'Explique o motivo da devolução',
  });

export const legalTemplateRetireInput = z.object({
  reason: z.string().trim().min(5).max(2000),
});

/** Link de origem no Google Drive (só referência; a versão do BackOffice é independente). */
export const legalTemplateDriveUrl = z
  .string()
  .trim()
  .max(500)
  .regex(/^https:\/\/(drive|docs)\.google\.com\//, 'Use um link do Google Drive ou Docs')
  .or(z.literal(''))
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();

/** Gerar o documento da solicitação a partir de uma minuta vigente (ADR 0059). */
/** Gerar o documento: a minuta principal e, se houver, os anexos dela que entram (por minuta). */
export const legalGenerateInput = z.object({
  templateVersionId: z.uuid(),
  annexTemplateIds: z.array(z.uuid()).max(10).default([]),
  /** Cláusula padrão escolhida para cada marcação {{clausula.<finalidade>}} (versão aprovada). */
  clauses: z.partialRecord(z.enum(LEGAL_CLAUSE_PURPOSES), z.uuid()).default({}),
});

/**
 * Dia-limite para emitir a nota fiscal nos documentos gerados (padrão da LOOT, decisão de
 * 28/10/2026). Provisório: vale até Compras registrar o combinado com cada fornecedor.
 */
export const LEGAL_DEFAULT_NF_DAY = 25;

/**
 * Marcação de cláusula padrão na minuta (janela de ajuste, nível b; decisões de 28/10/2026):
 * {{clausula.<finalidade>}} recebe, na geração, o texto da cláusula aprovada escolhida pela equipe.
 */
export const CLAUSE_SLOT_PREFIX = 'clausula.';
export function clauseSlotPurpose(key: string): (typeof LEGAL_CLAUSE_PURPOSES)[number] | null {
  if (!key.startsWith(CLAUSE_SLOT_PREFIX)) return null;
  const purpose = key.slice(CLAUSE_SLOT_PREFIX.length);
  return (LEGAL_CLAUSE_PURPOSES as readonly string[]).includes(purpose)
    ? (purpose as (typeof LEGAL_CLAUSE_PURPOSES)[number])
    : null;
}
